import type {
  AdminCompanyDetail,
  AdminCompanySummary,
  AdminHistoryEntry,
  AdminPerson,
  GiveableRole,
} from '../../types'
import { ApiError } from '../../types'
import { checkSlug as slugRule, checkDomain as domainRule, slugFrom as fromName, tidyDomain as tidy } from '../../lib/companyRules'

/**
 * Setting up companies, offline.
 *
 * It refuses everything the deployed one refuses, including the short name
 * rules and a domain already claimed. A stand-in kinder than the product is
 * worse than no stand-in: it teaches the screen that a mistake is fine, and
 * the mistake is then found by a customer.
 */

export interface MockLogin {
  userId: string
  tenant: string
  email: string
  name: string
  role: GiveableRole
  password: string
  /** Set when the password came from a super admin rather than the person. */
  temporary: boolean
  disabled?: boolean
  addedBy?: string
  addedAt?: string
}

export interface MockCompany {
  slug: string
  name: string
  createdAt: string
  domains: string[]
  people: MockLogin[]
  history: AdminHistoryEntry[]
  /** The single sign-on connection its people log in through (STE-1022). */
  ssoConnection?: string
}

export interface AdminDb {
  companies: MockCompany[]
}

/**
 * The demo company, holding the two people the sign in screen tells you about.
 *
 * They are really here rather than listed as a count of zero. A companies
 * screen that says a company has nobody in it, while two of its people can
 * sign in, is a screen teaching somebody to distrust the number.
 */
export const freshAdmin = (demoPassword: string): AdminDb => ({
  companies: [
    {
      slug: 'stello.in',
      name: 'Stello',
      createdAt: '2026-01-12',
      domains: ['stello.in'],
      people: [
        {
          userId: 'u-admin', tenant: 'stello.in', email: 'hr@stello.in', name: 'HR Manager',
          role: 'admin', password: demoPassword, temporary: false, addedAt: '2026-01-12',
        },
        {
          userId: 'u-manager', tenant: 'stello.in', email: 'manager@stello.in',
          name: 'People Manager', role: 'manager', password: demoPassword, temporary: false,
          addedAt: '2026-01-12',
        },
      ],
      history: [],
    },
  ],
})

/* ---------- the rules ---------- */

// The same ones the form checks and the same ones the API enforces, from one
// place. Three copies of a rule is three chances for one of them to be wrong.
export { checkSlug, slugFrom, tidyDomain, checkDomain, weakPassword, TEMPORARY_DAYS } from '../../lib/companyRules'

const UPPER = 'ABCDEFGHJKLMNPQRSTUVWXYZ'
const LOWER = 'abcdefghijkmnopqrstuvwxyz'
const DIGIT = '23456789'
const ALPHABET = UPPER + LOWER + DIGIT

/** The same generator the deployed system uses, including what it leaves out. */
export function makePassword(length = 20): string {
  const bytes = new Uint8Array(length)
  crypto.getRandomValues(bytes)
  const pick = (set: string, at: number) => set[bytes[at] % set.length]
  const rest = Array.from({ length: length - 3 }, (_, i) => pick(ALPHABET, i + 3)).join('')
  return `${pick(UPPER, 0)}${pick(LOWER, 1)}${pick(DIGIT, 2)}${rest}`
}

const today = () => new Date().toISOString().slice(0, 10)
const id = (p: string) => `${p}_${Math.random().toString(36).slice(2, 10)}`

/* ---------- reading ---------- */

export const find = (db: AdminDb, slug: string) => db.companies.find((c) => c.slug === slug)

/**
 * Whether this address belongs to somebody who sets companies up.
 *
 * Offline there is no company of ours holding them, so the one signed in is
 * the one to compare against. Same answer, same refusal.
 */
export const isSuperAdminEmail = (email: string, signedInAs: string) =>
  email.trim().toLowerCase() === signedInAs.trim().toLowerCase()

export function must(db: AdminDb, slug: string): MockCompany {
  const found = find(db, slug)
  // Not found rather than refused, for the same reason the deployed one does
  // it: a refusal would confirm the company exists.
  if (!found) throw new ApiError(404, 'Not found.')
  return found
}

export const toPerson = (p: MockLogin, sso = false): AdminPerson => ({
  userId: p.userId,
  tenant: p.tenant,
  email: p.email,
  name: p.name,
  role: p.role,
  addedBy: p.addedBy,
  addedAt: p.addedAt,
  disabled: p.disabled,
  signedIn: !p.temporary,
  ...(sso ? { sso: true } : {}),
})

export const summarise = (db: AdminDb): AdminCompanySummary[] =>
  db.companies
    .map((c) => ({
      slug: c.slug,
      name: c.name,
      createdAt: c.createdAt,
      people: c.people.length,
      domains: [...c.domains].sort(),
    }))
    // Newest first, as the deployed one does.
    .sort((a, b) => (b.createdAt ?? '').localeCompare(a.createdAt ?? ''))

export const detail = (db: AdminDb, slug: string): AdminCompanyDetail => {
  const c = must(db, slug)
  return {
    company: { slug: c.slug, name: c.name, createdAt: c.createdAt, ssoConnection: c.ssoConnection ?? null },
    domains: [...c.domains].sort(),
    people: [...c.people].sort((a, b) => a.email.localeCompare(b.email)).map((p) => toPerson(p, Boolean(c.ssoConnection))),
    history: [...c.history].reverse().slice(0, 20),
    // Mock mode shows the choice, as an Auth0 environment does.
    ssoAvailable: true,
    setPasswordAvailable: true,
  }
}

/* ---------- writing ---------- */

export function note(
  c: MockCompany,
  who: { name: string; email: string },
  action: AdminHistoryEntry['action'],
  extra: Partial<AdminHistoryEntry> = {},
) {
  c.history.push({
    id: id('aud'),
    at: new Date().toISOString(),
    byName: who.name,
    byEmail: who.email,
    action,
    tenant: c.slug,
    ...extra,
  })
}

export function domainOwner(db: AdminDb, domain: string): MockCompany | undefined {
  return db.companies.find((c) => c.domains.includes(domain))
}

export function createCompany(
  db: AdminDb,
  who: { name: string; email: string },
  input: { name: string; slug: string; domains: string[]; adminEmail: string; adminName: string },
) {
  const name = input.name.trim()
  if (!name) throw new ApiError(400, 'A company name is needed.')
  // Not lower cased first. A capital is refused, as the deployed one does.
  const slug = (input.slug || fromName(name)).trim()
  const wrong = slugRule(slug)
  if (wrong) throw new ApiError(400, wrong)
  if (find(db, slug)) throw new ApiError(409, `The short name ${slug} is already taken.`)

  const domains = [...new Set(input.domains.map(tidy).filter(Boolean))]
  for (const d of domains) {
    const bad = domainRule(d)
    if (bad) throw new ApiError(400, bad)
    if (domainOwner(db, d)) throw new ApiError(409, `${d} already signs in to another company.`)
  }

  const adminEmail = input.adminEmail.trim().toLowerCase()
  if (!adminEmail.includes('@')) throw new ApiError(400, 'A first admin email address is needed.')

  const company: MockCompany = {
    slug, name, createdAt: today(), domains, people: [], history: [],
  }
  db.companies.push(company)
  note(company, who, 'company.created', { detail: { name } })
  for (const d of domains) note(company, who, 'domain.added', { detail: { domain: d } })

  const made = addPerson(db, who, slug, {
    email: adminEmail,
    name: input.adminName.trim() || adminEmail,
    role: 'admin',
  })
  return { company: { slug, name }, person: made.person, password: made.password }
}

export function addPerson(
  db: AdminDb,
  who: { name: string; email: string },
  slug: string,
  input: { email: string; name: string; role: GiveableRole },
) {
  const c = must(db, slug)
  const email = input.email.trim().toLowerCase()
  if (!email.includes('@')) throw new ApiError(400, 'An email address is needed.')
  if (input.role !== 'admin' && input.role !== 'manager') {
    throw new ApiError(400, 'Choose either admin or manager.')
  }
  if (c.people.some((p) => p.email === email)) {
    throw new ApiError(409, `${email} already has an account at this company.`)
  }

  const password = makePassword()
  const person: MockLogin = {
    userId: id('u'),
    tenant: slug,
    email,
    name: input.name.trim() || email,
    role: input.role,
    password,
    temporary: true,
    addedBy: who.email,
    addedAt: today(),
  }
  c.people.push(person)
  note(c, who, 'person.added', { personEmail: email, detail: { role: input.role } })
  // At a single sign-on company there is no password to hand over (STE-1022).
  if (c.ssoConnection) return { person: toPerson(person, true), sso: true as const }
  return { person: toPerson(person), password }
}

export function mustPerson(db: AdminDb, slug: string, userId: string): MockLogin {
  const found = must(db, slug).people.find((p) => p.userId === userId)
  if (!found) throw new ApiError(404, 'Not found.')
  return found
}
